/**
 * Comprehensive Cache Service with Compression and Offline Support
 * Optimized for field workers in desert areas with poor connectivity
 */

import * as LZString from 'lz-string';
import { snapshotService, SnapshotManifest } from './snapshots';
import { Well } from '../types';
// import { parseCompactWellsStream } from './compactParser';

// Priority counties (major oil-producing areas in New Mexico)
export const PRIORITY_COUNTIES = ['Eddy', 'Lea'];

// County adjacency map for intelligent prefetching
export const COUNTY_ADJACENCY: Record<string, string[]> = {
  'Eddy': ['Lea', 'Chaves', 'Otero'],
  'Lea': ['Eddy', 'Chaves', 'Roosevelt'],
  'Chaves': ['Eddy', 'Lea', 'Roosevelt', 'Lincoln', 'Otero'],
  'Roosevelt': ['Lea', 'Chaves', 'Curry', 'DeBaca'],
  'Otero': ['Eddy', 'Chaves', 'Lincoln', 'Sierra', 'Doña Ana'],
  'Lincoln': ['Chaves', 'Otero', 'Sierra', 'Socorro', 'Torrance'],
  'Curry': ['Roosevelt', 'Quay', 'DeBaca'],
  'DeBaca': ['Roosevelt', 'Curry', 'Guadalupe', 'Chaves'],
  'Guadalupe': ['DeBaca', 'Quay', 'San Miguel', 'Torrance'],
  'Quay': ['Curry', 'DeBaca', 'Guadalupe', 'Harding'],
  'Harding': ['Quay', 'Union', 'Colfax', 'Mora', 'San Miguel'],
  'Union': ['Harding', 'Colfax'],
  'Colfax': ['Union', 'Harding', 'Mora', 'Taos'],
  'Mora': ['Harding', 'Colfax', 'San Miguel', 'Taos', 'Rio Arriba'],
  'San Miguel': ['Guadalupe', 'Harding', 'Mora', 'Santa Fe', 'Torrance'],
  'Torrance': ['Guadalupe', 'San Miguel', 'Santa Fe', 'Lincoln', 'Socorro', 'Valencia'],
  'Santa Fe': ['San Miguel', 'Torrance', 'Sandoval', 'Rio Arriba', 'Los Alamos'],
  'Sandoval': ['Santa Fe', 'Rio Arriba', 'Los Alamos', 'Valencia', 'Bernalillo'],
  'Rio Arriba': ['Mora', 'Santa Fe', 'Sandoval', 'Los Alamos', 'Taos', 'San Juan'],
  'Los Alamos': ['Santa Fe', 'Sandoval', 'Rio Arriba'],
  'Taos': ['Colfax', 'Mora', 'Rio Arriba'],
  'San Juan': ['Rio Arriba', 'McKinley', 'Sandoval'],
  'McKinley': ['San Juan', 'Sandoval', 'Cibola', 'Valencia'],
  'Cibola': ['McKinley', 'Valencia', 'Socorro', 'Catron'],
  'Valencia': ['Sandoval', 'McKinley', 'Cibola', 'Torrance', 'Socorro', 'Bernalillo'],
  'Bernalillo': ['Sandoval', 'Valencia', 'Torrance', 'Santa Fe'],
  'Socorro': ['Torrance', 'Valencia', 'Cibola', 'Lincoln', 'Sierra', 'Catron'],
  'Sierra': ['Socorro', 'Lincoln', 'Otero', 'Doña Ana', 'Grant', 'Catron'],
  'Catron': ['Cibola', 'Socorro', 'Sierra', 'Grant'],
  'Grant': ['Catron', 'Sierra', 'Luna', 'Hidalgo'],
  'Doña Ana': ['Otero', 'Sierra', 'Luna'],
  'Luna': ['Doña Ana', 'Grant', 'Hidalgo'],
  'Hidalgo': ['Grant', 'Luna']
};

// All NM counties
export const ALL_COUNTIES = Object.keys(COUNTY_ADJACENCY);

// Facility types
export type FacilityTypeKey = 'wells' | 'swds' | 'rigs' | 'facilities';
export const FACILITY_TYPES: FacilityTypeKey[] = ['wells', 'swds', 'rigs', 'facilities'];

interface CacheMetadata {
  timestamp: number;
  version: string;
  county?: string;
  facilityType: FacilityTypeKey;
  compressedSize: number;
  uncompressedSize: number;
  checksum: string;
  complete: boolean;
  connectionType?: string;
}

interface CacheStatus {
  cached: boolean;
  metadata?: CacheMetadata;
  isStale: boolean;
  age?: number; // hours
}

interface DownloadProgress {
  county: string;
  facilityType: FacilityTypeKey;
  bytesDownloaded: number;
  bytesPerSecond: number;
  estimatedSecondsRemaining: number;
  complete: boolean;
  percentComplete?: number; // Overall percent across all tasks (0-100)
  taskComplete?: boolean;   // Whether this county/type finished
  currentBatch?: number;    // Current batch index (1-based)
  totalBatches?: number;    // Total batches for this county/type when known
}

interface CacheExport {
  version: string;
  timestamp: number;
  data: Record<string, { metadata: CacheMetadata; compressed: string }>;
  checksum: string;
}

class CacheService {
  private db: IDBDatabase | null = null;
  private readonly DB_NAME = 'ZiaWellNavCache';
  private readonly DB_VERSION = 1;
  private readonly STORE_FACILITIES = 'facilities';
  private readonly STORE_SEARCHES = 'searches';
  private readonly STORE_METADATA = 'metadata';
  private readonly CACHE_VERSION = '1.3.0'; // Force refresh to fix Unknown wells issue
  private readonly CACHE_EXPIRY_HOURS = 168; // 7 days - field workers may be offline for extended periods
  private readonly SEARCH_EXPIRY_DAYS = 7;
  private readonly SNAPSHOT_BASE = (import.meta as any).env?.VITE_SNAPSHOT_BASE_URL || 'https://snapshots.ziawellnav.pages.dev';
  
  private downloadStartTimes: Map<string, number> = new Map();
  private downloadByteCounts: Map<string, number> = new Map();

  async initialize(): Promise<void> {
    if (this.db) return;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Facilities cache store (county + type combinations)
        if (!db.objectStoreNames.contains(this.STORE_FACILITIES)) {
          const facilityStore = db.createObjectStore(this.STORE_FACILITIES, { keyPath: 'key' });
          facilityStore.createIndex('county', 'county', { unique: false });
          facilityStore.createIndex('facilityType', 'facilityType', { unique: false });
          facilityStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        // Search results cache store
        if (!db.objectStoreNames.contains(this.STORE_SEARCHES)) {
          const searchStore = db.createObjectStore(this.STORE_SEARCHES, { keyPath: 'key' });
          searchStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        // Metadata store for sync status, etc.
        if (!db.objectStoreNames.contains(this.STORE_METADATA)) {
          db.createObjectStore(this.STORE_METADATA, { keyPath: 'key' });
        }
      };
    });
  }

  /** Return total county count (for progress calculations/UI) */
  getAllCountyCount(): number {
    return ALL_COUNTIES.length;
  }

  /**
   * Generate simple checksum for data integrity
   */
  private generateChecksum(data: string): string {
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Compress data using LZ-string
   */
  private compress(data: Well[]): string {
    const json = JSON.stringify(data);
    return LZString.compressToUTF16(json);
  }

  /**
   * Decompress data using LZ-string
   */
  private decompress(compressed: string): Well[] {
    const json = LZString.decompressFromUTF16(compressed);
    if (!json) throw new Error('Failed to decompress data');
    return JSON.parse(json);
  }

  /**
   * Generate cache key - includes state to prevent county name collisions
   * County names are not unique across states (e.g., "Anderson" exists in TX, TN, etc.)
   * By including state in the key, TX.Midland and NM.Midland can coexist in cache
   */
  private getCacheKey(county: string | undefined, facilityType: FacilityTypeKey, state?: string): string {
    // Default to NM for backward compatibility, but use state from data when available
    const stateCode = state || 'NM';
    return `${facilityType}_${county || 'all'}_${stateCode}`;
  }

  /**
   * Cache facility data for a specific county and type
   */
  async cacheFacilityType(
    facilityType: FacilityTypeKey,
    county: string | undefined,
    data: Well[],
    connectionType?: string
  ): Promise<void> {
    try {
      await this.initialize();
      if (!this.db) throw new Error('Database not initialized');

      // Extract state from data if available (all wells should have same state for a given county)
      const state = data && data.length > 0 ? (data[0].state || 'NM') : 'NM';
      const key = this.getCacheKey(county, facilityType, state);
      const uncompressedSize = new Blob([JSON.stringify(data)]).size;
      const compressed = this.compress(data);
      const compressedSize = new Blob([compressed]).size;
      const checksum = this.generateChecksum(compressed);

      const metadata: CacheMetadata = {
        timestamp: Date.now(),
        version: this.CACHE_VERSION,
        county,
        facilityType,
        compressedSize,
        uncompressedSize,
        checksum,
        complete: true,
        connectionType
      };

      const transaction = this.db.transaction([this.STORE_FACILITIES], 'readwrite');
      const store = transaction.objectStore(this.STORE_FACILITIES);

      await new Promise<void>((resolve, reject) => {
        const request = store.put({
          key,
          county,
          facilityType,
          state,
          compressed,
          metadata,
          timestamp: Date.now()
        });

        // Add timeout to prevent hanging
        const timeout = setTimeout(() => {
          reject(new Error('Cache write timeout'));
        }, 5000);

        request.onsuccess = () => {
          clearTimeout(timeout);
          resolve();
        };

        request.onerror = () => {
          clearTimeout(timeout);
          console.error(`❌ Cache write error for ${key}:`, request.error);
          resolve(); // Resolve anyway, caching failure shouldn't break the app
        };

        transaction.onerror = () => {
          clearTimeout(timeout);
          console.error(`❌ Transaction error for ${key}:`, transaction.error);
          resolve();
        };

        transaction.onabort = () => {
          clearTimeout(timeout);
          console.error(`❌ Transaction aborted for ${key}`);
          resolve();
        };
      });

      console.log(`✅ Cached ${facilityType} for ${county || 'all'} (${state}): ${(compressedSize / 1024).toFixed(1)} KB (${((1 - compressedSize / uncompressedSize) * 100).toFixed(0)}% compression)`);
    } catch (error) {
      console.error('❌ cacheFacilityType error:', error);
      // Caching failure shouldn't break the app
    }
  }

  /**
   * Get cached facility data - tries both TX and NM states if not specified
   */
  async getCachedData(county: string | undefined, facilityType: FacilityTypeKey, state?: string): Promise<Well[] | null> {
    try {
      await this.initialize();
      if (!this.db) return null;

      // Try specified state first, then NM (default), then TX
      const statesToTry = state ? [state] : ['NM', 'TX'];
      
      for (const tryState of statesToTry) {
        const key = this.getCacheKey(county, facilityType, tryState);
        const transaction = this.db.transaction([this.STORE_FACILITIES], 'readonly');
        const store = transaction.objectStore(this.STORE_FACILITIES);

        const result = await new Promise<any>((resolve, reject) => {
          const request = store.get(key);

          // Add timeout to prevent hanging
          const timeout = setTimeout(() => {
            reject(new Error('Cache read timeout'));
          }, 5000);

          request.onsuccess = () => {
            clearTimeout(timeout);
            resolve(request.result);
          };

          request.onerror = () => {
            clearTimeout(timeout);
            resolve(null);
          };

          transaction.onerror = () => {
            clearTimeout(timeout);
            resolve(null);
          };
        });

        if (result) {
          // Invalidate stale cache entries when the cache version changes
          if (result.metadata?.version !== this.CACHE_VERSION) {
            console.warn(`⚠️ Cache version mismatch for ${key} (have ${result.metadata?.version}, need ${this.CACHE_VERSION}) — ignoring cached data`);
            continue;
          }

          try {
            // Verify checksum
            const calculatedChecksum = this.generateChecksum(result.compressed);
            if (calculatedChecksum !== result.metadata.checksum) {
              console.warn(`⚠️ Checksum mismatch for ${key}, cache may be corrupted`);
              continue;
            }

            const data = this.decompress(result.compressed);
            console.log(`✅ Retrieved ${data.length} items from cache for ${key}`);
            return data;
          } catch (error) {
            console.error(`❌ Failed to decompress cache for ${key}:`, error);
            continue;
          }
        }
      }

      return null;
    } catch (error) {
      console.error('❌ getCachedData error:', error);
      return null;
    }
  }


  /**
   * Merge cached facilities across all base types for a county (or statewide).
   * Useful for speeding up "All" searches without hitting the network.
   */
  async getCachedAllFacilities(county?: string, state?: string): Promise<{ data: Well[]; counts: Record<FacilityTypeKey, number> }> {
    const [wells, swds, rigs, facilities] = await Promise.all([
      this.getCachedData(county, 'wells', state),
      this.getCachedData(county, 'swds', state),
      this.getCachedData(county, 'rigs', state),
      this.getCachedData(county, 'facilities', state)
    ]);

    const counts: Record<FacilityTypeKey, number> = {
      wells: wells?.length || 0,
      swds: swds?.length || 0,
      rigs: rigs?.length || 0,
      facilities: facilities?.length || 0
    };

    const data = [
      ...(wells || []),
      ...(swds || []),
      ...(rigs || []),
      ...(facilities || [])
    ];

    if (data.length > 0) {
      console.log(`✅ Retrieved cached ALL facilities for ${county || 'all counties'}:`, counts);
    } else {
      console.log(`ℹ️ No cached ALL facilities found for ${county || 'all counties'}`);
    }

    return { data, counts };
  }

  /**
   * Check if data is cached and valid
   */
  async isCached(county: string | undefined, facilityType: FacilityTypeKey): Promise<boolean> {
    const status = await this.getCacheStatus(county, facilityType);
    return status.cached && !status.isStale;
  }

  /**
   * Get cache status with metadata
   */
  async getCacheStatus(county: string | undefined, facilityType: FacilityTypeKey): Promise<CacheStatus> {
    await this.initialize();
    if (!this.db) return { cached: false, isStale: false };

    const key = this.getCacheKey(county, facilityType);
    const transaction = this.db.transaction([this.STORE_FACILITIES], 'readonly');
    const store = transaction.objectStore(this.STORE_FACILITIES);

    return new Promise((resolve) => {
      const request = store.get(key);

      request.onsuccess = () => {
        const result = request.result;
        if (!result) {
          resolve({ cached: false, isStale: false });
          return;
        }

        const age = (Date.now() - result.metadata.timestamp) / (1000 * 60 * 60); // hours
        const isStale = age > this.CACHE_EXPIRY_HOURS;

        resolve({
          cached: true,
          metadata: result.metadata,
          isStale,
          age
        });
      };

      request.onerror = () => resolve({ cached: false, isStale: false });
    });
  }

  /**
   * Get all cached counties for a facility type
   */
  async getCachedCounties(facilityType: FacilityTypeKey): Promise<string[]> {
    await this.initialize();
    if (!this.db) return [];

    const transaction = this.db.transaction([this.STORE_FACILITIES], 'readonly');
    const store = transaction.objectStore(this.STORE_FACILITIES);
    const index = store.index('facilityType');

    return new Promise((resolve, reject) => {
      const request = index.getAll(facilityType);

      request.onsuccess = () => {
        const counties = request.result
          .filter(item => item.county && !this.isExpired(item.metadata.timestamp))
          .map(item => item.county as string);
        resolve([...new Set(counties)]);
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Check if timestamp is expired
   */
  private isExpired(timestamp: number): boolean {
    const age = (Date.now() - timestamp) / (1000 * 60 * 60); // hours
    return age > this.CACHE_EXPIRY_HOURS;
  }

  /**
   * Fetch all records from the facilities API using limit/offset pagination.
   * This runs client-side to avoid Worker CPU overruns from huge single responses.
   */
  private async fetchAllOnce(baseUrl: string, connectionType?: string): Promise<Well[]> {
    const url = baseUrl;
    const started = Date.now();
    const response = await fetch(url, {
      headers: connectionType ? { 'X-Connection-Type': connectionType } : undefined
    });

    if (!response.ok) {
      throw new Error(`Fetch failed ${response.status} for ${url}`);
    }

    const json = await response.json();
    const facilities: Well[] = json.facilities || [];
    const elapsed = (Date.now() - started) / 1000;
    console.log(`📥 Downloaded ${facilities.length} items from ${url} in ${elapsed.toFixed(1)}s`);
    return facilities;
  }

  /**
   * Download prebuilt snapshots from CDN/R2 and cache them. Useful for full offline packs.
   */
  async cacheAllFromSnapshots(
    onProgress?: (progress: DownloadProgress) => void,
    manifest?: SnapshotManifest
  ): Promise<void> {
    const manifestData = manifest || await snapshotService.fetchManifest(this.SNAPSHOT_BASE);
    const filesByType: Record<FacilityTypeKey, string> = {
      wells: '',
      swds: '',
      rigs: '',
      facilities: ''
    };

    for (const file of manifestData.files) {
      if (file.facilityType in filesByType && !filesByType[file.facilityType as FacilityTypeKey]) {
        filesByType[file.facilityType as FacilityTypeKey] = file.url;
      }
    }

    let completed = 0;
    const total = FACILITY_TYPES.length;

    for (const facilityType of FACILITY_TYPES) {
      const url = filesByType[facilityType];
      if (!url) {
        console.warn(`No snapshot file for ${facilityType} in manifest ${manifestData.version}`);
        continue;
      }

      const entry = manifestData.files.find(f => f.url === url) || {
        url,
        size: 0,
        sha256: '',
        facilityType
      };

      const started = Date.now();
      const data = await snapshotService.downloadSnapshot(entry);
      const bytesDownloaded = new Blob([JSON.stringify(data)]).size;
      await this.cacheFacilityType(facilityType, undefined, data, this.getConnectionType());

      completed++;
      const elapsedSeconds = (Date.now() - started) / 1000;
      const bytesPerSecond = bytesDownloaded / Math.max(elapsedSeconds, 0.1);
      const estimatedSecondsRemaining = bytesPerSecond > 0 ? ((total - completed) * bytesDownloaded) / bytesPerSecond : 0;
      const percentComplete = (completed / total) * 100;

      if (onProgress) {
        onProgress({
          county: 'ALL',
          facilityType,
          bytesDownloaded,
          bytesPerSecond,
          estimatedSecondsRemaining,
          percentComplete,
          taskComplete: true,
          currentBatch: undefined,
          totalBatches: undefined,
          complete: completed === total
        });
      }
    }
  }

  /**
   * Cache all facilities for all counties (used during initial download)
   */
  async cacheAllFacilities(
    onProgress?: (progress: DownloadProgress) => void,
    priorityOnly: boolean = false,
    includeAllWellsCompact: boolean = false,
    preferSnapshots: boolean = false
  ): Promise<void> {
    const checkpointKey = 'cache_checkpoint_v1';
    const savedCheckpointRaw = localStorage.getItem(checkpointKey);
    const savedCheckpoint = savedCheckpointRaw ? JSON.parse(savedCheckpointRaw) as { priorityOnly: boolean; countyIndex: number; facilityIndex: number; counties: string[]; preferSnapshots: boolean; mode: 'snapshots' | 'api' } : null;

    const usingSnapshots = !priorityOnly && preferSnapshots;

    if (!priorityOnly && preferSnapshots) {
      console.log('🌐 Using snapshot CDN for full dataset');
      try {
        await this.cacheAllFromSnapshots(onProgress);
        localStorage.removeItem(checkpointKey);
        return;
      } catch (err) {
        console.warn('Snapshot download failed, falling back to API pagination:', err);
      }
    }

    const counties = priorityOnly ? PRIORITY_COUNTIES : ALL_COUNTIES;
    const connectionType = this.getConnectionType();

    let startCountyIndex = 0;
    let startFacilityIndex = 0;
    if (savedCheckpoint && savedCheckpoint.priorityOnly === priorityOnly && savedCheckpoint.mode === (usingSnapshots ? 'snapshots' : 'api')) {
      startCountyIndex = Math.min(savedCheckpoint.countyIndex || 0, counties.length - 1);
      startFacilityIndex = Math.min(savedCheckpoint.facilityIndex || 0, FACILITY_TYPES.length - 1);
      console.log(`🔁 Resuming download from checkpoint county index ${startCountyIndex}, facility index ${startFacilityIndex}`);
    } else {
      localStorage.removeItem(checkpointKey);
    }

    let completedCount = startCountyIndex * FACILITY_TYPES.length + startFacilityIndex;
    let bytesCompleted = 0;
    const totalTasks = counties.length * FACILITY_TYPES.length;

    // 30-second pause after priority counties
    let pauseAfterPriority = priorityOnly ? false : true;

    // Optionally cache the global all-wells dataset using the compact stream first
    if (includeAllWellsCompact) {
      try {
        const count = await this.cacheAllWellsCompact(connectionType);
        console.log(`💾 Cached all wells (compact): ${count}`);
      } catch (error) {
        console.error('❌ Failed to cache all wells (compact):', error);
      }
    }

    for (let ci = startCountyIndex; ci < counties.length; ci++) {
      const county = counties[ci];
      // Pause after priority counties before continuing with others
      if (pauseAfterPriority && county === PRIORITY_COUNTIES[PRIORITY_COUNTIES.length - 1]) {
        console.log('⏸️ Pausing 30 seconds before continuing with remaining counties...');
        await new Promise(resolve => setTimeout(resolve, 30000));
      }

      const facilityStartIndex = ci === startCountyIndex ? startFacilityIndex : 0;

      for (let fi = facilityStartIndex; fi < FACILITY_TYPES.length; fi++) {
        const facilityType = FACILITY_TYPES[fi];
        const downloadKey = `${county}_${facilityType}`;
        this.downloadStartTimes.set(downloadKey, Date.now());
        this.downloadByteCounts.set(downloadKey, 0);

        let taskBytes = 0;
        const taskStarted = Date.now();

        try {
          // Fetch full dataset from API (server handles its own pagination)
          const baseUrl = `/api/facilities/search?type=${facilityType}&county=${encodeURIComponent(county)}`;
          let data: Well[] = [];
          try {
            data = await this.fetchAllOnce(baseUrl, connectionType);
          } catch (err) {
            console.error(`Fetch failed for ${baseUrl}:`, err);
            continue; // skip to next
          }

          // Final download stats for this task
          taskBytes = new Blob([JSON.stringify(data)]).size;
          this.downloadByteCounts.set(downloadKey, taskBytes);

          const elapsedSeconds = (Date.now() - taskStarted) / 1000;
          const bytesPerSecond = taskBytes / Math.max(elapsedSeconds, 0.1);

          // Cache the data
          await this.cacheFacilityType(facilityType, county, data, connectionType);

          // Update aggregate counters after successful cache
          bytesCompleted += taskBytes;
          completedCount++;

          // Save checkpoint for resume
          localStorage.setItem(checkpointKey, JSON.stringify({
            priorityOnly,
            countyIndex: ci,
            facilityIndex: fi + 1 >= FACILITY_TYPES.length ? 0 : fi + 1,
            counties,
            preferSnapshots,
            mode: usingSnapshots ? 'snapshots' : 'api'
          }));

          const remainingTasks = totalTasks - completedCount;
          const avgBytesPerTask = completedCount > 0 ? bytesCompleted / completedCount : taskBytes;
          const estimatedSecondsRemaining = bytesPerSecond > 0 ? (avgBytesPerTask * remainingTasks) / bytesPerSecond : 0;
          const percentComplete = (completedCount / totalTasks) * 100;

          if (onProgress) {
            onProgress({
              county,
              facilityType,
              bytesDownloaded: taskBytes,
              bytesPerSecond,
              estimatedSecondsRemaining,
              percentComplete,
              taskComplete: true,
              currentBatch: undefined,
              totalBatches: undefined,
              complete: completedCount === totalTasks
            });
          }
        } catch (error) {
          console.error(`❌ Failed to cache ${facilityType} for ${county}:`, error);
        }

        // Clean up tracking
        this.downloadStartTimes.delete(downloadKey);
        this.downloadByteCounts.delete(downloadKey);
      }

      // Prefetch adjacent counties in background (don't await)
      if (COUNTY_ADJACENCY[county]) {
        this.prefetchAdjacentCounties(county).catch(err => 
          console.error(`Failed to prefetch adjacent counties for ${county}:`, err)
        );
      }
    }

    // Clear checkpoint when fully done
    localStorage.removeItem(checkpointKey);
  }

  /**
   * Cache the entire wells dataset using the compact streaming endpoint (no county filter)
   */
  async cacheAllWellsCompact(connectionType?: string): Promise<number> {
    const baseUrl = '/api/facilities/search?type=wells';
    const wells = await this.fetchAllOnce(baseUrl, connectionType);
    await this.cacheFacilityType('wells', undefined, wells, connectionType);
    return wells.length;
  }

  /**
   * Prefetch adjacent counties in background
   */
  private async prefetchAdjacentCounties(county: string): Promise<void> {
    const adjacentCounties = COUNTY_ADJACENCY[county] || [];
    const connectionType = this.getConnectionType();

    // Only prefetch on good connections
    if (connectionType === '2g' || connectionType === 'slow-2g') {
      return;
    }

    for (const adjacentCounty of adjacentCounties) {
      for (const facilityType of FACILITY_TYPES) {
        const isCached = await this.isCached(adjacentCounty, facilityType);
        if (!isCached) {
          try {
            const url = `/api/facilities/search?type=${facilityType}&county=${encodeURIComponent(adjacentCounty)}`;
            const response = await fetch(url);
            const json = await response.json();

            if (json.success && json.facilities) {
              await this.cacheFacilityType(facilityType, adjacentCounty, json.facilities, connectionType);
            }
          } catch (error) {
            console.error(`Failed to prefetch ${facilityType} for ${adjacentCounty}:`, error);
          }
        }
      }
    }
  }

  /**
   * Get connection type
   */
  private getConnectionType(): string {
    if ('connection' in navigator) {
      const conn = (navigator as any).connection;
      return conn?.effectiveType || 'unknown';
    }
    return 'unknown';
  }

  /**
   * Get connection quality (for UI display)
   */
  getConnectionQuality(): { type: string; downlink: number; fast: boolean } {
    if ('connection' in navigator) {
      const conn = (navigator as any).connection;
      const type = conn?.effectiveType || 'unknown';
      const downlink = conn?.downlink || 0;
      const fast = downlink >= 2 || type === '4g' || type === '5g';
      return { type, downlink, fast };
    }
    return { type: 'unknown', downlink: 0, fast: true };
  }

  /**
   * Verify cache integrity
   */
  async verifyIntegrity(): Promise<{ corrupted: string[]; fixed: string[] }> {
    await this.initialize();
    if (!this.db) return { corrupted: [], fixed: [] };

    const corrupted: string[] = [];
    const fixed: string[] = [];

    const transaction = this.db.transaction([this.STORE_FACILITIES], 'readwrite');
    const store = transaction.objectStore(this.STORE_FACILITIES);

    return new Promise((resolve, reject) => {
      const request = store.getAll();

      request.onsuccess = async () => {
        for (const item of request.result) {
          const calculatedChecksum = this.generateChecksum(item.compressed);
          if (calculatedChecksum !== item.metadata.checksum) {
            corrupted.push(item.key);
            
            // Try to re-download
            try {
              const url = `/api/facilities/search?type=${item.facilityType}&county=${encodeURIComponent(item.county || '')}`;
              const response = await fetch(url);
              const json = await response.json();

              if (json.success && json.facilities) {
                await this.cacheFacilityType(item.facilityType, item.county, json.facilities);
                fixed.push(item.key);
              }
            } catch (error) {
              console.error(`Failed to fix corrupted cache for ${item.key}:`, error);
            }
          }
        }

        resolve({ corrupted, fixed });
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Export cache for sharing
   * @param format 'compressed' for binary UTF16 (smaller file) or 'json' for human-readable
   */
  async exportCache(format: 'compressed' | 'json' = 'compressed'): Promise<Blob> {
    await this.initialize();
    if (!this.db) throw new Error('Database not initialized');

    const transaction = this.db.transaction([this.STORE_FACILITIES], 'readonly');
    const store = transaction.objectStore(this.STORE_FACILITIES);

    return new Promise((resolve, reject) => {
      const request = store.getAll();

      request.onsuccess = () => {
        const data: Record<string, { metadata: CacheMetadata; compressed: string }> = {};
        
        for (const item of request.result) {
          data[item.key] = {
            metadata: item.metadata,
            compressed: item.compressed
          };
        }

        const exportData: CacheExport = {
          version: this.CACHE_VERSION,
          timestamp: Date.now(),
          data,
          checksum: this.generateChecksum(JSON.stringify(data))
        };

        const json = JSON.stringify(exportData, null, format === 'json' ? 2 : 0);
        
        let blob: Blob;
        if (format === 'json') {
          // Human-readable JSON export
          blob = new Blob([json], { type: 'application/json' });
          console.log(`✅ Exported cache (JSON): ${(blob.size / 1024 / 1024).toFixed(2)} MB`);
        } else {
          // Compressed binary export (smaller file size)
          const compressed = LZString.compressToUTF16(json);
          blob = new Blob([compressed], { type: 'application/octet-stream' });
          console.log(`✅ Exported cache (compressed): ${(blob.size / 1024 / 1024).toFixed(2)} MB`);
        }
        
        resolve(blob);
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Import cache from shared file
   */
  async importCache(blob: Blob, onProgress?: (current: number, total: number) => void): Promise<void> {
    await this.initialize();
    if (!this.db) throw new Error('Database not initialized');

    const compressed = await blob.text();
    const json = LZString.decompressFromUTF16(compressed);
    if (!json) throw new Error('Failed to decompress import file');

    const exportData: CacheExport = JSON.parse(json);

    // Verify checksum
    const calculatedChecksum = this.generateChecksum(JSON.stringify(exportData.data));
    if (calculatedChecksum !== exportData.checksum) {
      throw new Error('Import file checksum mismatch - file may be corrupted');
    }

    const transaction = this.db.transaction([this.STORE_FACILITIES], 'readwrite');
    const store = transaction.objectStore(this.STORE_FACILITIES);

    const keys = Object.keys(exportData.data);
    let imported = 0;

    for (const key of keys) {
      const { metadata, compressed } = exportData.data[key];

      // Only import if newer than existing cache
      const existingRequest = store.get(key);
      await new Promise<void>((resolve) => {
        existingRequest.onsuccess = async () => {
          const existing = existingRequest.result;
          
          if (!existing || existing.metadata.timestamp < metadata.timestamp) {
            await new Promise<void>((resolveWrite) => {
              const writeRequest = store.put({
                key,
                county: metadata.county,
                facilityType: metadata.facilityType,
                compressed,
                metadata,
                timestamp: metadata.timestamp
              });

              writeRequest.onsuccess = () => {
                imported++;
                if (onProgress) onProgress(imported, keys.length);
                resolveWrite();
              };
              writeRequest.onerror = () => resolveWrite();
            });
          } else {
            imported++;
            if (onProgress) onProgress(imported, keys.length);
          }
          resolve();
        };
        existingRequest.onerror = () => resolve();
      });
    }

    console.log(`✅ Imported ${imported} cache entries from shared file`);
  }

  /**
   * Clear all facility caches
   */
  async clearCache(): Promise<void> {
    await this.initialize();
    if (!this.db) return;

    const transaction = this.db.transaction([this.STORE_FACILITIES], 'readwrite');
    const store = transaction.objectStore(this.STORE_FACILITIES);

    await new Promise<void>((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => {
        console.log('✅ Cleared all facility caches');
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear old search results (>7 days)
   */
  async clearSearchHistory(): Promise<void> {
    await this.initialize();
    if (!this.db) return;

    const cutoffTime = Date.now() - (this.SEARCH_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
    const transaction = this.db.transaction([this.STORE_SEARCHES], 'readwrite');
    const store = transaction.objectStore(this.STORE_SEARCHES);
    const index = store.index('timestamp');

    return new Promise((resolve, reject) => {
      const request = index.openCursor(IDBKeyRange.upperBound(cutoffTime));
      let deletedCount = 0;

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          cursor.delete();
          deletedCount++;
          cursor.continue();
        } else {
          console.log(`✅ Deleted ${deletedCount} old search results`);
          resolve();
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get total cache size
   */
  async getTotalCacheSize(): Promise<{ facilities: number; searches: number; total: number }> {
    await this.initialize();
    if (!this.db) return { facilities: 0, searches: 0, total: 0 };

    const facilitiesSize = await new Promise<number>((resolve) => {
      try {
        const tx = this.db!.transaction([this.STORE_FACILITIES], 'readonly');
        tx.onabort = () => resolve(0);
        tx.onerror = () => resolve(0);
        const store = tx.objectStore(this.STORE_FACILITIES);
        const request = store.getAll();
        request.onsuccess = () => {
          const size = (request.result || []).reduce((total: number, item: any) => total + (item.metadata?.compressedSize || 0), 0);
          resolve(size);
        };
        request.onerror = () => resolve(0);
      } catch (err) {
        console.warn('getTotalCacheSize facilities error', err);
        resolve(0);
      }
    });

    const searchesSize = await new Promise<number>((resolve) => {
      try {
        const tx = this.db!.transaction([this.STORE_SEARCHES], 'readonly');
        tx.onabort = () => resolve(0);
        tx.onerror = () => resolve(0);
        const store = tx.objectStore(this.STORE_SEARCHES);
        const request = store.getAll();
        request.onsuccess = () => {
          const size = (request.result || []).reduce((total: number, item: any) => total + new Blob([item.compressed || '']).size, 0);
          resolve(size);
        };
        request.onerror = () => resolve(0);
      } catch (err) {
        console.warn('getTotalCacheSize searches error', err);
        resolve(0);
      }
    });

    return {
      facilities: facilitiesSize,
      searches: searchesSize,
      total: facilitiesSize + searchesSize
    };
  }

  /**
   * Get last sync info
   */
  async getLastSyncInfo(): Promise<{ timestamp: number; batteryLevel?: number } | null> {
    await this.initialize();
    if (!this.db) return null;

    const transaction = this.db.transaction([this.STORE_METADATA], 'readonly');
    const store = transaction.objectStore(this.STORE_METADATA);

    return new Promise((resolve) => {
      const request = store.get('lastSync');
      request.onsuccess = () => resolve(request.result?.value || null);
      request.onerror = () => resolve(null);
    });
  }

  /**
   * Set last sync info
   */
  async setLastSyncInfo(timestamp: number, batteryLevel?: number): Promise<void> {
    await this.initialize();
    if (!this.db) return;

    const transaction = this.db.transaction([this.STORE_METADATA], 'readwrite');
    const store = transaction.objectStore(this.STORE_METADATA);

    await new Promise<void>((resolve, reject) => {
      const request = store.put({
        key: 'lastSync',
        value: { timestamp, batteryLevel }
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Check if sync is in progress
   */
  async isSyncInProgress(): Promise<boolean> {
    await this.initialize();
    if (!this.db) return false;

    const transaction = this.db.transaction([this.STORE_METADATA], 'readonly');
    const store = transaction.objectStore(this.STORE_METADATA);

    return new Promise((resolve) => {
      const request = store.get('syncInProgress');
      request.onsuccess = () => resolve(request.result?.value || false);
      request.onerror = () => resolve(false);
    });
  }

  /**
   * Set sync in progress flag
   */
  async setSyncInProgress(inProgress: boolean): Promise<void> {
    await this.initialize();
    if (!this.db) return;

    const transaction = this.db.transaction([this.STORE_METADATA], 'readwrite');
    const store = transaction.objectStore(this.STORE_METADATA);

    await new Promise<void>((resolve, reject) => {
      const request = store.put({
        key: 'syncInProgress',
        value: inProgress
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Cancel ongoing sync
   */
  async cancelSync(): Promise<void> {
    await this.setSyncInProgress(false);
    console.log('🛑 Sync cancelled');
  }

  // ============================================
  // LETTER-BASED INDEXING FOR FAST WELL BROWSING
  // ============================================

  /**
   * Get the well name from a well object (handles both 'wellName' and 'name' fields)
   * The API returns 'name' but the Well interface uses 'wellName'
   */
  private getWellName(well: Well | any): string {
    return well?.wellName || well?.name || '';
  }

  /**
   * Get the letter key for a well name (A-Z or # for numbers)
   */
  private getLetterKey(wellName: string): string {
    const firstChar = (wellName || '').charAt(0).toUpperCase();
    if (/[A-Z]/.test(firstChar)) {
      return firstChar;
    }
    // Numbers 0-9 and other characters go under '#'
    return '#';
  }

  /**
   * Get the letter key for a well object (handles both field names)
   */
  private getWellLetterKey(well: Well | any): string {
    return this.getLetterKey(this.getWellName(well));
  }

  /**
   * Get wells by letter from ALL cached counties combined
   * @param letter The letter to filter by (A-Z or '#' for numbers)
   * @param limit Max number of wells to return (for pagination)
   * @param offset Starting index (for pagination)
   * @returns Wells matching the letter, sorted alphabetically
   */
  async getWellsByLetter(
    letter: string,
    secondLetter?: string,
    thirdLetter?: string,
    fourthLetter?: string,
    limit: number = 500,
    offset: number = 0
  ): Promise<{ wells: Well[]; total: number; hasMore: boolean; cachedCounties: string[] }> {
    await this.initialize();
    if (!this.db) return { wells: [], total: 0, hasMore: false, cachedCounties: [] };

    const allWells: Well[] = [];
    const cachedCounties: string[] = [];

    // Get all cached facility entries for wells
    const transaction = this.db.transaction([this.STORE_FACILITIES], 'readonly');
    const store = transaction.objectStore(this.STORE_FACILITIES);
    const index = store.index('facilityType');

    const entries = await new Promise<any[]>((resolve, reject) => {
      const request = index.getAll('wells');
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });

    // Decompress and filter wells by letter from each cached county
    // Note: We don't check expiry here - field workers need data even if "stale"
    for (const entry of entries) {
      if (!entry.compressed) continue;
      
      try {
        // Verify checksum
        const calculatedChecksum = this.generateChecksum(entry.compressed);
        if (calculatedChecksum !== entry.metadata?.checksum) continue;

        const wells = this.decompress(entry.compressed);
        const countyName = entry.county || 'All';
        
        if (!cachedCounties.includes(countyName)) {
          cachedCounties.push(countyName);
        }

        // Filter wells by letter (handle both 'name' and 'wellName' fields)
        const filtered = wells.filter((well: Well) => {
          const first = this.getWellLetterKey(well);
          if (first !== letter) return false;
          const name = this.getWellName(well);
          if (secondLetter && !(name.length >= 2 && name[1].toUpperCase() === secondLetter.toUpperCase())) return false;
          if (thirdLetter && !(name.length >= 3 && name[2].toUpperCase() === thirdLetter.toUpperCase())) return false;
          if (fourthLetter && !(name.length >= 4 && name[3].toUpperCase() === fourthLetter.toUpperCase())) return false;
          return true;
        });
        allWells.push(...filtered);
      } catch (error) {
        console.error(`Failed to process cache entry:`, error);
      }
    }

    // Sort alphabetically by well name (handle both field names)
    allWells.sort((a, b) => this.getWellName(a).localeCompare(this.getWellName(b)));

    // Apply pagination
    const total = allWells.length;
    const paginatedWells = allWells.slice(offset, offset + limit);
    const hasMore = offset + limit < total;

    console.log(`📖 Letter "${letter}": ${paginatedWells.length} wells (${offset}-${offset + paginatedWells.length} of ${total}) from ${cachedCounties.length} cached counties`);

    return {
      wells: paginatedWells,
      total,
      hasMore,
      cachedCounties
    };
  }

  /**
   * Get letter counts across ALL cached counties
   * Returns counts for each letter that has wells
   * @returns Object with letter keys and counts, plus metadata
   */
  async getCachedLetterCounts(): Promise<{
    counts: Record<string, number>;
    secondCounts: Record<string, Record<string, number>>; // first -> { second -> count }
    thirdCounts: Record<string, Record<string, Record<string, number>>>; // first -> second -> third -> count
    fourthCounts: Record<string, Record<string, Record<string, Record<string, number>>>>; // first -> second -> third -> fourth -> count
    totalWells: number;
    cachedCounties: string[];
    letters: string[]; // Sorted list of letters with data
  }> {
    await this.initialize();
    if (!this.db) return { counts: {}, secondCounts: {}, thirdCounts: {}, fourthCounts: {}, totalWells: 0, cachedCounties: [], letters: [] };

    const counts: Record<string, number> = {};
    const secondCounts: Record<string, Record<string, number>> = {};
    const thirdCounts: Record<string, Record<string, Record<string, number>>> = {};
    const fourthCounts: Record<string, Record<string, Record<string, Record<string, number>>>> = {};
    const cachedCounties: string[] = [];
    let totalWells = 0;

    // Get all cached facility entries for wells
    const transaction = this.db.transaction([this.STORE_FACILITIES], 'readonly');
    const store = transaction.objectStore(this.STORE_FACILITIES);
    const index = store.index('facilityType');

    const entries = await new Promise<any[]>((resolve, reject) => {
      const request = index.getAll('wells');
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });

    // Process each cached county
    // Note: We don't check expiry here - field workers need data even if "stale"
    for (const entry of entries) {
      if (!entry.compressed) continue;

      try {
        // Verify checksum
        const calculatedChecksum = this.generateChecksum(entry.compressed);
        if (calculatedChecksum !== entry.metadata?.checksum) continue;

        const wells = this.decompress(entry.compressed);
        const countyName = entry.county || 'All';

        if (!cachedCounties.includes(countyName)) {
          cachedCounties.push(countyName);
        }

        // Count wells by letter (handle both 'name' and 'wellName' fields)
        for (const well of wells) {
          const letter = this.getWellLetterKey(well);
          counts[letter] = (counts[letter] || 0) + 1;

          // Track second-letter counts for two-letter navigation (skip if name too short)
          const name = this.getWellName(well);
          if (name && name.length >= 2 && letter !== '#') {
            const second = name[1].toUpperCase();
            if (!secondCounts[letter]) secondCounts[letter] = {};
            secondCounts[letter][second] = (secondCounts[letter][second] || 0) + 1;

            if (name.length >= 3) {
              const third = name[2].toUpperCase();
              if (!thirdCounts[letter]) thirdCounts[letter] = {};
              if (!thirdCounts[letter][second]) thirdCounts[letter][second] = {};
              thirdCounts[letter][second][third] = (thirdCounts[letter][second][third] || 0) + 1;

              if (name.length >= 4) {
                const fourth = name[3].toUpperCase();
                if (!fourthCounts[letter]) fourthCounts[letter] = {};
                if (!fourthCounts[letter][second]) fourthCounts[letter][second] = {};
                if (!fourthCounts[letter][second][third]) fourthCounts[letter][second][third] = {};
                fourthCounts[letter][second][third][fourth] = (fourthCounts[letter][second][third][fourth] || 0) + 1;
              }
            }
          }
          totalWells++;
        }
      } catch (error) {
        console.error(`Failed to process cache entry for letter counts:`, error);
      }
    }

    // Generate sorted list of letters (# first, then A-Z)
    const letters = Object.keys(counts).sort((a, b) => {
      if (a === '#') return -1;
      if (b === '#') return 1;
      return a.localeCompare(b);
    });

    console.log(`📊 Letter counts: ${totalWells.toLocaleString()} wells across ${cachedCounties.length} counties, ${letters.length} letters with data`);

    return {
      counts,
      secondCounts,
      thirdCounts,
      fourthCounts,
      totalWells,
      cachedCounties,
      letters
    };
  }

  /**
   * Get all cached wells count without full decompression (faster)
   */
  async getCachedWellsCount(): Promise<{
    totalWells: number;
    cachedCounties: string[];
    byCounty: Record<string, number>;
  }> {
    await this.initialize();
    if (!this.db) return { totalWells: 0, cachedCounties: [], byCounty: {} };

    const byCounty: Record<string, number> = {};
    const cachedCounties: string[] = [];
    let totalWells = 0;

    const transaction = this.db.transaction([this.STORE_FACILITIES], 'readonly');
    const store = transaction.objectStore(this.STORE_FACILITIES);
    const index = store.index('facilityType');

    const entries = await new Promise<any[]>((resolve, reject) => {
      const request = index.getAll('wells');
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });

    for (const entry of entries) {
      // Note: We don't check expiry here - field workers need data even if \"stale\"
      if (!entry.compressed) continue;

      try {
        const calculatedChecksum = this.generateChecksum(entry.compressed);
        if (calculatedChecksum !== entry.metadata?.checksum) continue;

        const wells = this.decompress(entry.compressed);
        const countyName = entry.county || 'All';
        const count = wells.length;

        if (!cachedCounties.includes(countyName)) {
          cachedCounties.push(countyName);
        }

        byCounty[countyName] = count;
        totalWells += count;
      } catch (error) {
        console.error(`Failed to count wells for cache entry:`, error);
      }
    }

    return { totalWells, cachedCounties, byCounty };
  }

  /**
   * Get wells from a specific cached county by letter
   */
  async getCountyWellsByLetter(county: string, letter: string): Promise<Well[]> {
    const cached = await this.getCachedData(county, 'wells');
    if (!cached) return [];

    return cached
      .filter(well => this.getWellLetterKey(well) === letter)
      .sort((a, b) => this.getWellName(a).localeCompare(this.getWellName(b)));
  }

  /**
   * Search cached wells by partial name match
   */
  async searchCachedWells(query: string, limit: number = 100): Promise<Well[]> {
    await this.initialize();
    if (!this.db || !query.trim()) return [];

    const searchTerm = query.toLowerCase().trim();
    const results: Well[] = [];

    const transaction = this.db.transaction([this.STORE_FACILITIES], 'readonly');
    const store = transaction.objectStore(this.STORE_FACILITIES);
    const index = store.index('facilityType');

    const entries = await new Promise<any[]>((resolve, reject) => {
      const request = index.getAll('wells');
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });

    for (const entry of entries) {
      if (results.length >= limit) break;
      // Note: We don't check expiry here - field workers need data even if "stale"
      if (!entry.compressed) continue;

      try {
        const calculatedChecksum = this.generateChecksum(entry.compressed);
        if (calculatedChecksum !== entry.metadata?.checksum) continue;

        const wells = this.decompress(entry.compressed);
        
        for (const well of wells) {
          if (results.length >= limit) break;
          
          const wellName = this.getWellName(well);
          const matchName = wellName.toLowerCase().includes(searchTerm);
          const matchApi = (well.api || (well as any).identifier || '').toLowerCase().includes(searchTerm);
          // Only match wellName and apiNumber - NOT operator (business names cause irrelevant matches)
          
          if (matchName || matchApi) {
            results.push(well);
          }
        }
      } catch (error) {
        console.error(`Failed to search cache entry:`, error);
      }
    }

    // Sort by relevance (name matches first)
    results.sort((a, b) => {
      const aName = this.getWellName(a).toLowerCase();
      const bName = this.getWellName(b).toLowerCase();
      const aNameMatch = aName.startsWith(searchTerm);
      const bNameMatch = bName.startsWith(searchTerm);
      if (aNameMatch && !bNameMatch) return -1;
      if (!aNameMatch && bNameMatch) return 1;
      return aName.localeCompare(bName);
    });

    console.log(`🔍 Cache search "${query}": ${results.length} results`);
    return results;
  }
}

// Export singleton instance
export const cacheService = new CacheService();
